import React, { useState, useEffect, useMemo } from "react";
import "./Content.scss";

const Content = ({
  data,
  formValidation = true,
  formClass,
  formatters = {},
  validators = {},
  onChange,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Memoized content value calculation
  const contentValue = useMemo(() => {
    if (data?.html) {
      return data.html;
    }
    if (data?.content) {
      return data.content;
    }
    if (data?.defaultValue) {
      return data.defaultValue;
    }
    return "";
  }, [data?.key, data?.html, data?.content, data?.defaultValue]);

  // Apply custom formatter if provided
  const formattedContent = useMemo(() => {
    if (data?.formatter && formatters[data.formatter]) {
      const formatter = formatters[data.formatter];
      if (typeof formatter === "function") {
        return formatter(contentValue);
      }
    }
    return contentValue;
  }, [contentValue, data?.formatter, formatters]);

  // Sanitize HTML content - basic sanitization
  const sanitizeHTML = useMemo(() => {
    if (!formattedContent) return "";
    
    // Basic HTML sanitization - remove dangerous elements and attributes
    let sanitized = formattedContent;
    
    // Remove script tags and their content
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
    
    // Remove dangerous attributes
    sanitized = sanitized.replace(/on\w+="[^"]*"/gi, "");
    sanitized = sanitized.replace(/on\w+='[^']*'/gi, "");
    sanitized = sanitized.replace(/javascript:/gi, "");
    
    // Remove dangerous elements
    sanitized = sanitized.replace(/<(iframe|embed|object|applet|form|input|button)\b[^>]*>/gi, "");
    sanitized = sanitized.replace(/<\/(iframe|embed|object|applet|form|input|button)>/gi, "");
    
    return sanitized;
  }, [formattedContent]);

  // Label positioning utilities (similar to TextField)
  const labelStyles = useMemo(() => {
    const position = data?.labelPosition || "top";
    const isHorizontal = [
      "left-left",
      "left-right", 
      "right-left",
      "right-right",
    ].includes(position);

    const containerStyle = {
      top: {
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
      },
      bottom: {
        flexDirection: "column-reverse",
        justifyContent: "flex-start",
        alignItems: "stretch",
      },
      "left-left": {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-start",
      },
      "left-right": {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-start",
      },
      "right-left": {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "flex-start",
      },
      "right-right": {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "flex-start",
      },
    }[position] || {
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch",
    };

    const labelStyle = {
      width: isHorizontal && data?.labelWidth ? `${data.labelWidth}%` : "auto",
      textAlign: ["left-right", "right-right"].includes(position)
        ? "right"
        : "left",
      marginRight:
        ["left-left", "left-right"].includes(position) && data?.labelMargin
          ? `${data.labelMargin}%`
          : "0",
      marginLeft:
        ["right-left", "right-right"].includes(position) && data?.labelMargin
          ? `${data.labelMargin}%`
          : "0",
      paddingTop: ["top", "bottom"].includes(position) ? "12px" : "0",
    };

    return { containerStyle, labelStyle };
  }, [data?.labelPosition, data?.labelWidth, data?.labelMargin]);

  // Handle conditional logic
  useEffect(() => {
    // Handle conditional visibility
    if (data?.conditional?.show !== null) {
      // This would be handled by the parent form component
      // For now, we'll just show the content
      setIsVisible(!data?.hidden);
    } else {
      setIsVisible(!data?.hidden);
    }
  }, [data?.conditional, data?.hidden]);

  // Handle custom conditional logic
  useEffect(() => {
    if (data?.customConditional && typeof data.customConditional === "string") {
      try {
        // This would need to be implemented based on your form's conditional logic system
        // For now, just show the content
        setIsVisible(!data?.hidden);
      } catch (error) {
        console.warn("Custom conditional logic error:", error);
        setIsVisible(!data?.hidden);
      }
    }
  }, [data?.customConditional, data?.hidden]);

  // Don't render if hidden or not visible
  if (data?.hidden || !isVisible) {
    return null;
  }

  // Render label
  const renderLabel = () => {
    if (data?.hideLabel || !data?.label) return null;

    return (
      <div className="content__label" style={labelStyles.labelStyle}>
        {data?.labelPrefix && (
          <span className="content__label-prefix">{data.labelPrefix}</span>
        )}
        {data.label}
        {data?.tooltip && (
          <span className="content__tooltip" title={data.tooltip}>
            ℹ️
          </span>
        )}
      </div>
    );
  };

  // Render the main content
  const renderContent = () => {
    if (!sanitizeHTML) return null;

    return (
      <div 
        className={`content__html ${data?.customClass || ""}`}
        dangerouslySetInnerHTML={{ __html: sanitizeHTML }}
        style={data?.overlay?.style ? JSON.parse(data.overlay.style) : {}}
      />
    );
  };

  // Render description if provided
  const renderDescription = () => {
    if (!data?.description) return null;

    return (
      <div className="content__description">
        <div dangerouslySetInnerHTML={{ __html: data.description }} />
      </div>
    );
  };

  // Render overlay if configured
  const renderOverlay = () => {
    if (!data?.overlay || !data.overlay.style) return null;

    const overlayStyles = {
      position: "absolute",
      left: data.overlay.left || "0",
      top: data.overlay.top || "0", 
      width: data.overlay.width || "100%",
      height: data.overlay.height || "100%",
      ...JSON.parse(data.overlay.style || "{}")
    };

    return <div className="content__overlay" style={overlayStyles} />;
  };

  return (
    <div
      className={`content ${formClass || ""} ${
        data?.disabled ? "content--disabled" : ""
      }`}
      data-key={data?.key}
      data-type="content"
    >
      <div className="content__container" style={labelStyles.containerStyle}>
        {renderLabel()}
        <div className="content__field">
          {renderContent()}
          {renderDescription()}
          {renderOverlay()}
        </div>
      </div>
    </div>
  );
};

export default Content;